import React, { useState, useMemo, useEffect } from "react";
import {
	Box,
	FormControl,
	Typography,
	Chip,
	Menu,
	MenuItem,
	TextField,
	Divider,
	Checkbox,
	IconButton,
	Button,
	CircularProgress,
} from "@mui/material";
import {
	ArrowDropDown,
	ArrowDropUp,
	Search,
	MoreHoriz,
	ArrowBack,
	Delete,
} from "@mui/icons-material";
import { useTheme, alpha, darken, lighten } from "@mui/material/styles";
import InternoModalConfirmacaoSaida from "../../../components/modalSaindoDaPagina";

// IMPORTANTE: Importe sua instância do Axios aqui
import api from "../../../services/api";

export default function TagSelector({
	selectedTags = [], // Tags selecionadas (vem do pai)
	onChange, // Função para atualizar a seleção no pai
	type, // Tipo da tag (0: VM, 1: DB, etc) - IMPORTANTE
	title,
	subtitle,
	selecaoUnica = false, // true = uma tag so por vez (regra atual do produto do cliente)
}) {
	const theme = useTheme();

	// --- ESTADOS DE DADOS ---
	const [availableTags, setAvailableTags] = useState([]);
	const [loading, setLoading] = useState(false);
	const [actionLoading, setActionLoading] = useState(false); // Para load de criar/editar

	// --- ESTADOS DE UI ---
	const [anchorEl, setAnchorEl] = useState(null);
	const [menuWidth, setMenuWidth] = useState(null);
	const [menuMode, setMenuMode] = useState("list"); // 'list' | 'edit'
	const [searchTag, setSearchTag] = useState("");
	const [editingTag, setEditingTag] = useState(null);
	const [openDeleteModal, setOpenDeleteModal] = useState(false);

	// Cores para o Seletor
	const tagColors = [
		theme.palette.colors.red.light,
		theme.palette.colors.blue.medium,
		theme.palette.colors.orange.medium,
		theme.palette.colors.green.light,
		theme.palette.colors.pink.light,
		theme.palette.colors.yellow.dark,
		theme.palette.colors.gray.dark,
	];

	// -------------------------------------------------------------------------
	// 1. CARREGAR TAGS (READ)
	// -------------------------------------------------------------------------
	useEffect(() => {
		let isMounted = true;

		async function fetchTags() {
			if (type === undefined || type === null) return;

			setLoading(true);
			try {
				// ROTA SUGERIDA: POST /form/getTags { type: "0" }
				// Se você ainda não tem essa rota, o array virá vazio.
				// Ajuste a rota conforme seu backend.
				const response = await api.post("/form/getTags", {
					type: String(type),
				});

				if (isMounted && response.data) {
					setAvailableTags(response.data);
				}
			} catch (error) {
				console.error("Erro ao buscar tags:", error);
				// Fallback silencioso ou Mock temporário se necessário
				// setAvailableTags([]);
			} finally {
				if (isMounted) setLoading(false);
			}
		}

		fetchTags();

		return () => {
			isMounted = false;
		};
	}, [type]);
	console.log(availableTags);
	// -------------------------------------------------------------------------
	// 2. CRIAR TAG (CREATE)
	// -------------------------------------------------------------------------
	const handleCreate = async () => {
		if (!searchTag.trim()) return;

		try {
			setActionLoading(true);
			const defaultColor = theme.palette.colors.gray.dark;

			const response = await api.post("/form/createTags", {
				name: searchTag,
				color: defaultColor,
				type: String(type), // Garante envio como String
			});

			const newTag = response.data;

			// 1. Adiciona na lista de opções
			setAvailableTags((prev) => [...prev, newTag]);

			// 2. Já seleciona a tag automaticamente
			onChange(selecaoUnica ? [newTag] : [...selectedTags, newTag]);

			setSearchTag("");
		} catch (error) {
			console.error("Erro ao criar tag:", error);
		} finally {
			setActionLoading(false);
		}
	};

	// -------------------------------------------------------------------------
	// 3. ATUALIZAR TAG (UPDATE)
	// -------------------------------------------------------------------------
	const handleSaveEdit = async () => {
		if (!editingTag || !editingTag.name.trim()) return;

		try {
			setActionLoading(true);

			// ROTA SUGERIDA: PUT /form/updateTags
			await api.put("/form/updateTags", {
				id: editingTag?.id,
				name: editingTag?.name,
				color: editingTag?.color,
			});

			// Atualiza lista local
			setAvailableTags((prev) =>
				prev.map((t) => (t.id === editingTag.id ? editingTag : t)),
			);

			// Se a tag editada estava selecionada, atualiza no pai também (para refletir cor/nome)
			const isSelected = selectedTags.find((t) => t.id === editingTag.id);
			if (isSelected) {
				const newSelection = selectedTags.map((t) =>
					t.id === editingTag.id ? editingTag : t,
				);
				onChange(newSelection);
			}

			setMenuMode("list");
		} catch (error) {
			console.error("Erro ao editar tag:", error);
		} finally {
			setActionLoading(false);
		}
	};

	// -------------------------------------------------------------------------
	// 4. DELETAR TAG (DELETE)
	// -------------------------------------------------------------------------
	const handleDelete = async () => {
		if (!editingTag) return;

		try {
			setActionLoading(true);

			// ROTA SUGERIDA: POST /form/deleteTags { id: "..." }
			await api.post("/form/deleteTags", { id: editingTag.id });

			// Remove da lista local
			setAvailableTags((prev) => prev.filter((t) => t.id !== editingTag.id));

			// Remove da seleção do pai (se estiver selecionada)
			onChange(selectedTags.filter((t) => t.id !== editingTag.id));

			setMenuMode("list");
		} catch (error) {
			console.error("Erro ao deletar tag:", error);
		} finally {
			setActionLoading(false);
			setOpenDeleteModal(false);
		}
	};

	// --- LÓGICA DE UI ---

	const handleOpenMenu = (event) => {
		setMenuWidth(event.currentTarget.clientWidth);
		setAnchorEl(event.currentTarget);
	};

	const handleCloseMenu = () => {
		setAnchorEl(null);
		setTimeout(() => {
			setMenuMode("list");
			setSearchTag("");
			setEditingTag(null);
		}, 200); // Pequeno delay para animação fechar
	};

	const handleEnterEditMode = (e, tag) => {
		e.stopPropagation();
		setEditingTag({ ...tag });
		setMenuMode("edit");
	};

	const toggleTag = (tag) => {
		const isSelected = selectedTags.some((t) => t.id === tag.id);
		if (isSelected) {
			onChange(selectedTags.filter((t) => t.id !== tag.id));
		} else {
			onChange(selecaoUnica ? [tag] : [...selectedTags, tag]);
		}
	};

	// Filtro de busca local
	const filteredTags = useMemo(() => {
		if (!searchTag) return availableTags;
		return availableTags.filter((t) =>
			t.name.toLowerCase().includes(searchTag.toLowerCase()),
		);
	}, [availableTags, searchTag]);

	return (
		<>
			<FormControl fullWidth size="small">
				{/* --- TRIGGER BOX (Caixa de Seleção) --- */}
				<Box
					onClick={handleOpenMenu}
					sx={{
						border:
							theme.palette.mode === "dark"
								? `1px solid ${theme.palette.colors.white.lighter}40`
								: `1px solid ${theme.palette.colors.gray.dark}90`,
						borderRadius: "4px",
						minHeight: "40px",
						display: "flex",
						alignItems: "center",
						px: 1,
						py: 0.5,
						gap: 0.5,
						flexWrap: "wrap",
						cursor: "pointer",
						"&:hover": { borderColor: "text.primary" },
						position: "relative",
					}}
				>
					{/* Placeholder / Subtítulo */}
					{selectedTags.length === 0 && (
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								width: "100%",
								alignItems: "center",
							}}
						>
							<Typography color="text.secondary" sx={{ ml: 1, fontSize: 14 }}>
								{loading ? "Carregando tags..." : subtitle}
							</Typography>
							{loading ? (
								<CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
							) : Boolean(anchorEl) ? (
								<ArrowDropUp fontSize="small" sx={{ fontSize: "1.5rem" }} />
							) : (
								<ArrowDropDown fontSize="small" sx={{ fontSize: "1.5rem" }} />
							)}
						</Box>
					)}

					{/* Chips das Tags Selecionadas */}
					{selectedTags.map((tag) => (
						<Chip
							key={tag.id}
							label={tag.name}
							size="small"
							onDelete={(e) => {
								e.stopPropagation();
								toggleTag(tag);
							}}
							sx={{
								bgcolor: alpha(tag?.color, 0.2),
								color: tag?.color,
								fontWeight: "bold",
								border: `1px solid ${tag?.color}`,
								zIndex: 1,
							}}
						/>
					))}

					{/* Seta quando tem tags selecionadas */}
					{selectedTags.length > 0 && (
						<Box sx={{ ml: "auto" }}>
							{Boolean(anchorEl) ? <ArrowDropUp /> : <ArrowDropDown />}
						</Box>
					)}
				</Box>

				{/* Label Flutuante */}
				<Typography
					variant="caption"
					sx={{
						position: "absolute",
						top: -10,
						left: 10,
						backgroundColor: "background.paper",
						px: 0.5,
						color: "text.secondary",
						fontSize: "0.75rem",
						fontWeight: "bold",
					}}
				>
					{title}
				</Typography>

				{/* --- MENU DROPDOWN --- */}
				<Menu
					anchorEl={anchorEl}
					open={Boolean(anchorEl)}
					onClose={handleCloseMenu}
					PaperProps={{
						sx: {
							width: menuWidth || 300,
							maxHeight: 400,
							overflowX: "hidden",
							bgcolor:
								theme.palette.mode === "dark"
									? lighten(theme.palette.background.paper, 0.04)
									: darken(theme.palette.background.paper, 0.06),
						},
					}}
				>
					{menuMode === "list" ? (
						/* --- MODO LISTA --- */
						<Box>
							<Box sx={{ p: 1, pb: 0 }}>
								<TextField
									size="small"
									fullWidth
									placeholder="Buscar ou criar..."
									value={searchTag}
									onChange={(e) => setSearchTag(e.target.value)}
									autoFocus
									InputProps={{
										startAdornment: (
											<Search
												fontSize="small"
												sx={{ mr: 1, color: "text.secondary" }}
											/>
										),
										endAdornment: actionLoading && (
											<CircularProgress size={16} />
										),
									}}
									onKeyDown={(e) => {
										if (
											e.key === "Enter" &&
											filteredTags.length === 0 &&
											!actionLoading
										) {
											e.preventDefault();
											handleCreate();
										}
									}}
								/>
							</Box>

							<Divider sx={{ my: 1 }} />

							{/* Opção de Criar Nova Tag */}
							{filteredTags.length === 0 && searchTag && (
								<MenuItem onClick={handleCreate} disabled={actionLoading}>
									<Typography variant="body2" color="primary">
										{actionLoading ? "Criando..." : `Criar "${searchTag}"`}
									</Typography>
								</MenuItem>
							)}

							{/* Mensagem Vazia */}
							{filteredTags.length === 0 && !searchTag && (
								<Typography
									variant="caption"
									sx={{
										p: 2,
										display: "block",
										textAlign: "center",
										color: "text.secondary",
									}}
								>
									{loading ? "Carregando..." : "Nenhuma tag encontrada"}
								</Typography>
							)}

							{/* Lista de Tags */}
							{filteredTags.map((tag) => (
								<MenuItem
									key={tag.id}
									onClick={() => toggleTag(tag)}
									sx={{
										display: "flex",
										justifyContent: "space-between",
										bgcolor: alpha(theme.palette.background.paper, 0.07),
										"&:hover .edit-icon": { opacity: 1 },
									}}
								>
									<Box
										sx={{
											display: "flex",
											alignItems: "center",
											overflow: "hidden",
										}}
									>
										<Checkbox
											checked={selectedTags.some((t) => t.id === tag.id)}
											size="small"
											sx={{ p: 0.5, mr: 0.5 }}
										/>
										<Chip
											label={tag.name}
											size="small"
											sx={{
												bgcolor: alpha(tag.color, 0.2),
												color: tag.color,
												fontWeight: 600,
												maxWidth: 160,
											}}
										/>
									</Box>
									<IconButton
										size="small"
										className="edit-icon"
										onClick={(e) => handleEnterEditMode(e, tag)}
										sx={{ opacity: 0.5, transition: "0.2s" }}
									>
										<MoreHoriz fontSize="small" />
									</IconButton>
								</MenuItem>
							))}
						</Box>
					) : (
						/* --- MODO EDIÇÃO --- */
						<Box sx={{ p: 1 }}>
							<Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
								<IconButton
									size="small"
									onClick={() => setMenuMode("list")}
									sx={{ mr: 1 }}
								>
									<ArrowBack fontSize="small" />
								</IconButton>
								<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
									Editar Tag
								</Typography>
							</Box>

							{editingTag && (
								<>
									<TextField
										label="Nome da Tag"
										value={editingTag.name}
										onChange={(e) =>
											setEditingTag((prev) => ({
												...prev,
												name: e.target.value,
											}))
										}
										size="small"
										fullWidth
										sx={{ mb: 2 }}
									/>

									<Typography
										variant="caption"
										sx={{ ml: 1, mb: 1, display: "block" }}
									>
										Cor
									</Typography>

									<Box
										sx={{
											display: "flex",
											flexWrap: "wrap",
											gap: 1,
											px: 1,
											mb: 3,
										}}
									>
										{tagColors.map((color) => (
											<Box
												key={color} // Key movida para o wrapper para evitar warning
												sx={{
													width: 30,
													height: 30,
													borderRadius: "50%",
													bgcolor:
														editingTag.color === color ? color : "transparent",
													cursor: "pointer",
													display: "flex",
													justifyContent: "center",
													alignItems: "center",
													border:
														editingTag.color === color
															? "none"
															: "1px solid transparent",
												}}
											>
												<Box
													onClick={() =>
														setEditingTag((prev) => ({ ...prev, color }))
													}
													sx={{
														width: 24,
														height: 24,
														borderRadius: "50%",
														bgcolor: color,
														cursor: "pointer",
														border:
															editingTag.color === color
																? "3px solid " + theme.palette.background.paper
																: "none",
														boxShadow:
															editingTag.color === color
																? "0 0 0 2px " + color
																: "none",
													}}
												/>
											</Box>
										))}
									</Box>

									<Divider sx={{ my: 1, mb: 2 }} />

									<Box
										sx={{ display: "flex", justifyContent: "space-between" }}
									>
										<Button
											size="small"
											color="error"
											startIcon={<Delete />}
											onClick={() => setOpenDeleteModal(true)}
											disabled={actionLoading}
										>
											Excluir
										</Button>
										<Button
											size="small"
											variant="contained"
											onClick={handleSaveEdit}
											disabled={actionLoading}
										>
											{actionLoading ? "Salvando..." : "Salvar"}
										</Button>
									</Box>
								</>
							)}
						</Box>
					)}
				</Menu>
			</FormControl>

			<InternoModalConfirmacaoSaida
				openConfirmacao={openDeleteModal}
				setOpenConfirmacao={setOpenDeleteModal}
				confirmLoad={actionLoading}
				execute={handleDelete}
				cancel={() => setOpenDeleteModal(false)}
				title="Atenção"
				contentText="Tem certeza que deseja excluir essa tag permanentemente? Essa ação é irreversível."
				confirmText="Sim, excluir"
			/>
		</>
	);
}
